import { useMemo } from 'react';

import type { SponsorshipMode } from '@infinex-private/connect-types';
import { BottomSheetModal } from '@infinex-private/connect-web-shared/components';
import { FeeAccountFilledIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Emblem } from '@infinex/ui/components';
import { calculateFormattedAssetDecimalPrecision } from '@infinex/web-shared/utils';

import type { UserNativeBalanceDetails } from '../../types';

interface PaymentMethodOptionProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  balanceDisplay: string;
  isDisabled: boolean;
  onSelect: () => void;
}

function PaymentMethodOption({
  icon,
  title,
  subtitle,
  balanceDisplay,
  isDisabled,
  onSelect,
}: PaymentMethodOptionProps) {
  return (
    <button
      type="button"
      onClick={() => !isDisabled && onSelect()}
      disabled={isDisabled}
      className={cn(
        'bg-surface flex w-full items-center justify-between rounded-2xl p-4 transition-colors',
        isDisabled
          ? 'cursor-default opacity-100'
          : 'hover:bg-surfaceHover cursor-pointer'
      )}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex flex-col items-start">
          <div className="flex items-center">
            <span className="text-emphasis text-base font-semibold">
              {title}
            </span>
            {isDisabled && (
              <div className="bg-fill ml-2 flex items-center rounded-full px-2 py-1">
                <span className="text-default text-xs font-semibold">
                  Unavailable
                </span>
              </div>
            )}
          </div>
          {subtitle && (
            <span
              className={cn(
                'text-sm',
                isDisabled ? 'text-disabled' : 'text-secondary font-semibold'
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      <span
        className={cn(
          'text-base font-semibold',
          isDisabled ? 'text-secondary' : 'text-emphasis'
        )}
      >
        {balanceDisplay}
      </span>
    </button>
  );
}

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPaymentMethod: SponsorshipMode;
  gasAccountBalance: number | null;
  userBalance?: UserNativeBalanceDetails;
  supportedSponsorshipModes: SponsorshipMode[] | undefined;
  onSelectPaymentMethod: (method: SponsorshipMode) => void;
}

export function PaymentMethodModal({
  isOpen,
  onClose,
  gasAccountBalance,
  userBalance,
  supportedSponsorshipModes,
  onSelectPaymentMethod,
}: PaymentMethodModalProps) {
  const formattedNativeBalance = useMemo(() => {
    if (!userBalance) return '0';

    const { decimalBalance, balanceUsd } = userBalance;
    if (decimalBalance === 0) return '0';

    if (!balanceUsd) {
      return decimalBalance.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      });
    }

    const rateUsd = balanceUsd / decimalBalance;
    const decimals = calculateFormattedAssetDecimalPrecision({
      tokenAmount: decimalBalance.toString(),
      rateUsd,
    });

    return decimalBalance.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  }, [userBalance]);

  const isNativeSupported = supportedSponsorshipModes?.includes('native');
  const isAccountSupported = supportedSponsorshipModes?.includes('account');

  return (
    <BottomSheetModal open={isOpen} onClose={onClose} title="Pay costs with">
      <div className="flex flex-col gap-2 p-4">
        <PaymentMethodOption
          icon={
            <div className="bg-canvas flex size-10 items-center justify-center rounded-full">
              <FeeAccountFilledIcon
                fill={!isAccountSupported ? 'secondary' : 'default'}
              />
            </div>
          }
          title="Gas account"
          subtitle={
            isAccountSupported
              ? undefined
              : "Can't be used for this transaction"
          }
          balanceDisplay={`$${(gasAccountBalance ?? 0).toFixed(2)}`}
          isDisabled={!isAccountSupported}
          onSelect={() => onSelectPaymentMethod('account')}
        />

        <PaymentMethodOption
          icon={
            userBalance ? (
              <Emblem
                chain={userBalance.chainKey}
                size="large"
                src={userBalance.imgUrl}
              />
            ) : (
              <div className="bg-canvas flex size-10 items-center justify-center rounded-full" />
            )
          }
          title={userBalance?.name || 'Native token'}
          subtitle={
            isNativeSupported
              ? `${formattedNativeBalance} ${userBalance?.symbol || ''}`
              : "Can't be used for this transaction"
          }
          balanceDisplay={`$${(userBalance?.balanceUsd ?? 0).toFixed(2)}`}
          isDisabled={!isNativeSupported}
          onSelect={() => onSelectPaymentMethod('native')}
        />
      </div>
    </BottomSheetModal>
  );
}
